import { connect } from "react-redux";
import {
  Title,
  Filler,
  Legend,
  Tooltip,
  LinearScale,
  LineElement,
  PointElement,
  CategoryScale,
  Chart as ChartJS,
} from "chart.js";
import { Line } from "react-chartjs-2";
import {
  getDaySales,
  getDayTimeDetails,
} from "../../internals/managers/ReportsManager";
import useThemeVersion from "../../internals/hooks/useThemeVersion";

ChartJS.register(
  Title,
  Filler,
  Legend,
  Tooltip,
  LinearScale,
  LineElement,
  PointElement,
  CategoryScale
);

// Chart.js needs concrete color strings — resolve the design tokens at render
// time so the chart follows the active (light/dark) theme.
const cssVar = (name: string, fallback: string) => {
  const v = getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
  return v || fallback;
};

const LineChart = (props: any) => {
  const { dayOrders } = props;
  useThemeVersion();
  const labels = getDayTimeDetails(dayOrders);
  const sales = getDaySales(labels, dayOrders);

  const brand = cssVar('--brand', '#4E45E4');
  const brand2 = cssVar('--brand2', '#6F63FF');
  const text3 = cssVar('--text3', '#8C8C9B');

  const options: any = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "#272848",
        titleColor: "#ACADC4",
        bodyColor: "#ffffff",
        titleFont: { size: 11, family: "Instrument Sans" },
        bodyFont: { size: 13, weight: "bold", family: "Instrument Sans" },
        padding: 12,
        cornerRadius: 10,
        displayColors: false,
        callbacks: {
          label: (ctx: any) => ` Rs ${ctx.parsed.y.toLocaleString()}`,
        },
      },
    },
    scales: {
      y: {
        grid: {
          color: "rgba(172, 173, 196, 0.15)",
          drawBorder: false,
        },
        border: { dash: [4, 4], display: false },
        ticks: {
          padding: 12,
          font: { size: 11, family: "Instrument Sans" },
          color: text3,
          callback: (v: any) => `Rs ${v.toLocaleString()}`,
        },
      },
      x: {
        grid: { display: false },
        border: { display: false },
        ticks: {
          padding: 8,
          font: { size: 11, family: "Instrument Sans" },
          color: text3,
          maxRotation: 0,
        },
      },
    },
    elements: {
      point: {
        radius: 0,
        hoverRadius: 6,
        hoverBackgroundColor: brand,
        hoverBorderColor: "#ffffff",
        hoverBorderWidth: 2,
      },
    },
    interaction: {
      mode: "index",
      intersect: false,
    },
  };

  let width: any, height: any, gradient: any, bgGradient: any;

  const getLineGradient = (ctx: any, chartArea: any) => {
    const chartWidth = chartArea.right - chartArea.left;
    const chartHeight = chartArea.bottom - chartArea.top;
    if (!gradient || width !== chartWidth || height !== chartHeight) {
      width = chartWidth;
      height = chartHeight;
      gradient = ctx.createLinearGradient(0, chartArea.top, chartArea.right, chartArea.top);
      gradient.addColorStop(0, brand);
      gradient.addColorStop(1, brand2);
    }
    return gradient;
  };

  const getBgGradient = (ctx: any, chartArea: any) => {
    const chartWidth = chartArea.right - chartArea.left;
    const chartHeight = chartArea.bottom - chartArea.top;
    if (!bgGradient || width !== chartWidth || height !== chartHeight) {
      bgGradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
      bgGradient.addColorStop(0, "rgba(109, 113, 249, 0.18)");
      bgGradient.addColorStop(0.6, "rgba(109, 113, 249, 0.05)");
      bgGradient.addColorStop(1, "rgba(109, 113, 249, 0)");
    }
    return bgGradient;
  };

  const data = {
    labels,
    datasets: [
      {
        fill: true,
        data: sales,
        label: "Sales",
        tension: 0.45,
        cubicInterpolationMode: "monotone" as const,
        borderWidth: 2.5,
        borderColor: (context: any) => {
          const { ctx, chartArea } = context.chart;
          if (!chartArea) return brand;
          return getLineGradient(ctx, chartArea);
        },
        backgroundColor: (context: any) => {
          const { ctx, chartArea } = context.chart;
          if (!chartArea) return "rgba(109,113,249,0.1)";
          return getBgGradient(ctx, chartArea);
        },
      },
    ],
  };

  return <Line id="myChart" options={options} data={data} />;
};

export default connect(
  (state: any) => ({
    dayOrders: state.dashboard.get("dayOrders"),
  }),
  () => ({})
)(LineChart);
